import React from 'react'
import { Typography, Button, Card, App } from 'antd'
import { ArrowRightOutlined } from '@ant-design/icons'
import { useIntl } from 'react-intl'

import IntlMessages from '../../../../util/IntlMessages'
import { TeamsIcon, GoogleIcon, SlackIcon } from '../../../icons'
import { MicrosoftAuth } from '../../../../services/auth/microsoft/microsoft'
import { SlackAuth } from '../../../../services/auth/slack/slack'
import { GoogleAuth } from '../../../../services/auth/google/google'
import * as logger from '../../../../services/logger'
import { PlatformEnum } from '@vacationtracker/shared/data/platforms'

const { Title, Paragraph } = Typography

if (!process.env.REACT_APP_MICROSOFT_CLIENT_ID || !process.env.REACT_APP_SLACK_CLIENT_ID || !process.env.REACT_APP_GOOGLE_CLIENT_ID) {
  throw new Error('Client IDs are required')
}

const msAuth = new MicrosoftAuth(process.env.REACT_APP_MICROSOFT_CLIENT_ID)
const slackAuth = new SlackAuth(process.env.REACT_APP_SLACK_CLIENT_ID)
const googleAuth = new GoogleAuth(process.env.REACT_APP_GOOGLE_CLIENT_ID)

interface Platform {
  key: string
  name: React.ReactElement
  icon: React.ReactElement
  features: React.ReactElement[]
}

interface IntegrationStepProps {
  selectedPlatform: string | null
  connectedPlatform: string | null
  isLoggingIn: boolean
  onNext: () => void
  onSkipForNow: () => void
  onPlatformSelect: (platformKey: string) => void
  onConnectionSuccess: (platform: string, organizationId: string, token: string, thirdPartyId: string, newEmail?: string) => void
  onConnectionError: (error: Error | unknown, platform: string) => void
  onLoginStart: () => void
}

export const IntegrationStep: React.FC<IntegrationStepProps> = ({
  selectedPlatform,
  connectedPlatform,
  isLoggingIn,
  onNext,
  onSkipForNow,
  onPlatformSelect,
  onConnectionSuccess,
  onConnectionError,
  onLoginStart,
}) => {
  const { notification } = App.useApp()
  const { formatMessage } = useIntl()
  const platforms: Platform[] = [
    {
      key: PlatformEnum.Slack,
      name: <IntlMessages id="onboarding.adp.integration.platforms.slack.name" />,
      icon: <SlackIcon />,
      features: [
        <IntlMessages key="users" id="onboarding.adp.integration.platforms.slack.features.users" />,
        <IntlMessages key="sso" id="onboarding.adp.integration.platforms.slack.features.sso" />,
        <IntlMessages key="requests" id="onboarding.adp.integration.platforms.slack.features.requests" />,
        <IntlMessages key="alerts" id="onboarding.adp.integration.platforms.slack.features.alerts" />,
        <IntlMessages key="notifications" id="onboarding.adp.integration.platforms.slack.features.notifications" />,
        <IntlMessages key="bot" id="onboarding.adp.integration.platforms.slack.features.bot" />,
      ],
    },
    {
      key: PlatformEnum.Microsoft,
      name: <IntlMessages id="onboarding.adp.integration.platforms.microsoftTeams.name" />,
      icon: <TeamsIcon />,
      features: [
        <IntlMessages key="users" id="onboarding.adp.integration.platforms.microsoftTeams.features.users" />,
        <IntlMessages key="sso" id="onboarding.adp.integration.platforms.microsoftTeams.features.sso" />,
        <IntlMessages key="requests" id="onboarding.adp.integration.platforms.microsoftTeams.features.requests" />,
        <IntlMessages key="alerts" id="onboarding.adp.integration.platforms.microsoftTeams.features.alerts" />,
        <IntlMessages key="notifications" id="onboarding.adp.integration.platforms.microsoftTeams.features.notifications" />,
        <IntlMessages key="bot" id="onboarding.adp.integration.platforms.microsoftTeams.features.bot" />,
      ],
    },
    {
      key: PlatformEnum.Google,
      name: <IntlMessages id="onboarding.adp.integration.platforms.googleWorkspace.name" />,
      icon: <GoogleIcon />,
      features: [
        <IntlMessages key="users" id="onboarding.adp.integration.platforms.googleWorkspace.features.users" />,
        <IntlMessages key="sso" id="onboarding.adp.integration.platforms.googleWorkspace.features.sso" />,
      ],
    },
  ]

  const handleSigninError = (error: Error | unknown, platform: string) => {
    const errorMessage = error instanceof Error ? error.message : 'Unknown error occurred'
    logger.error('Platform connection error:', errorMessage)
    onConnectionError(error, platform)
  }

  const connectWithMicrosoft = (interactionRequired = false) => {
    const platform = PlatformEnum.Microsoft
    onLoginStart()

    msAuth.signin(['user.readbasic.all', 'team.readbasic.all'], interactionRequired)
      .then(([tokens, user, tenantId]) => {
        onConnectionSuccess(platform, tenantId as string, tokens.accessToken as string, user.id as string, user.mail)
      })
      .catch(e => handleSigninError(e, platform))
  }

  const connectWithSlack = () => {
    const platform = PlatformEnum.Slack
    onLoginStart()

    slackAuth.signin(['chat:write', 'commands', 'team:read', 'users:read', 'users:read.email'],
      ['channels:write', 'groups:write', 'users:read', 'users:read.email', 'team:read', 'channels:read', 'groups:read', 'users.profile:write'])
      .then(async (response) => {
        slackAuth.setUserToken(response.authed_user.access_token)
        slackAuth.setBotToken(response.access_token)
        const user = await slackAuth.getUser (response.authed_user.id,response.authed_user.access_token)
        onConnectionSuccess(platform, response.team.id, response.authed_user.access_token, user.id as string, user.profile.emai as string)
      })
      .catch(e => handleSigninError(e, platform))
  }

  const connectWithGoogle = () => {
    const platform = PlatformEnum.Google
    onLoginStart()

    googleAuth.signin(true)
      .then(() => {
        const googleUser = googleAuth.getSignedInUser()
        
        if (!googleUser.hd) {
          notification.error({
            message: formatMessage({ id: 'connect.google.notWorkspaceUserTitle' }),
            description: formatMessage({ id: 'connect.google.notWorkspaceUserDescription' }),
            duration: 0,
          })
          return
        }
        
        onConnectionSuccess(platform, googleUser.hd, googleAuth.getAccessToken(), googleUser.sub as string, googleUser.email as string)
      })
      .catch(e => handleSigninError(e, platform))
  }

  const handleLogin = () => {
    if (!selectedPlatform) return

    switch (selectedPlatform) {
      case PlatformEnum.Slack:
        connectWithSlack()
        break
      case PlatformEnum.Microsoft:
        connectWithMicrosoft()
        break
      case PlatformEnum.Google:
        connectWithGoogle()
        break
      default:
        logger.error('Unknown platform selected:', selectedPlatform)
    }
  }

  return (
    <div className="step-content-integration">
      <Title level={2} className="integration-title">
        <IntlMessages id="onboarding.adp.integration.title" />
      </Title>
      <Paragraph className="integration-description">
        <IntlMessages id="onboarding.adp.integration.description" />
      </Paragraph>
      
      <div className="platforms-grid">
        {platforms.map((platform) => (
          <Card
            key={platform.key}
            className={`platform-card ${selectedPlatform === platform.key ? 'selected' : ''} ${connectedPlatform === platform.key ? 'connected' : ''}`}
            onClick={() => onPlatformSelect(platform.key)}
          >
            {connectedPlatform === platform.key && (
              <div className="connected-badge">
                <IntlMessages id="onboarding.adp.integration.connected" />
              </div>
            )}
            <div className="platform-icon">
              {platform.icon}
            </div>
            <Title level={4} className="platform-name">
              {platform.name}
            </Title>
            <ul className="platform-features">
              {platform.features.map((feature, index) => (
                <li key={index}>- {feature}</li>
              ))}
            </ul>
          </Card>
        ))}
      </div>

      {selectedPlatform && !connectedPlatform && (
        <div className="login-section">
          <Button
            type="primary"
            size="large"
            loading={isLoggingIn}
            onClick={handleLogin}
            className="login-button"
          >
            <IntlMessages 
              id="onboarding.adp.integration.loginButton" 
              values={{ 
                platform: platforms.find(p => p.key === selectedPlatform)?.name || selectedPlatform, 
              }} 
            />
          </Button>
        </div>
      )}

      <div className="integration-actions">
        <Button
          type="primary"
          size="large"
          onClick={onNext}
          disabled={!connectedPlatform}
          className="continue-button"
        >
          <IntlMessages id="onboarding.adp.integration.continue" />
        </Button>
        {!connectedPlatform && (
          <Button
            type="link"
            size="large"
            onClick={onSkipForNow}
            className="skip-button"
          >
            <IntlMessages id="onboarding.adp.integration.skipForNow" /> <ArrowRightOutlined />
          </Button>
        )}
      </div>
    </div>
  )
}
